import { Body, breakpoints, Spacer } from '@spritz-finance/ui'
import React, { useMemo, useState } from 'react'
import styled, { css } from 'styled-components'
import plus from '../../../../assets/icons/plus-emoji.png'
import { FadePresence } from '../../../../components/FadePresence'
import { Icon } from '../../../../components/Icon'
import { Column } from '../../../../components/Layout/Column'
import { Row } from '../../../../components/Layout/Row'
import { PageLoading } from '../../../../components/PageLoading'
import { Switch } from '../../../../components/Switch'
import { useScreenSize } from '../../../../context/useScreenSize'
import { BillType } from '../../../../graphql/generated/graphql'
import { themedImage } from '../../../../theme/utils'

import { BillIcon } from '../bills/BillIcon'
import { useLinkBillContext } from './LinkBill'
import { PopularBills } from './PopularBills'
import { BillSelect, LinkBill } from './types'

const billTypes: BillSelect[] = [
    {
        title: 'Credit Card',
        secondaryLine: 'Add a credit card',
        merchantSearch: "Let's find your credit card provider",
        type: BillType.CreditCard,
    },
    {
        title: 'Car Payments',
        secondaryLine: 'Add a car payment',
        merchantSearch: "Let's find your auto loan provider",
        type: BillType.AutoLoan,
    },
    {
        title: 'Mortgage',
        secondaryLine: 'Add a mortgage',
        merchantSearch: "Let's find your mortgage provider",
        type: BillType.Mortgage,
    },
    {
        title: 'Student Loan',
        secondaryLine: 'Add a student loan',
        merchantSearch: "Let's find your student loan provider",
        type: BillType.StudentLoan,
    },
    {
        title: 'Utilities',
        secondaryLine: 'Add a utility bill',
        merchantSearch: "Let's find your utility provider",
        type: BillType.Utility,
    },
]

const Container = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
`

const Circle = styled.div`
    width: 35px;
    height: 35px;
    border-radius: 50%;
    background: var(--color-brand, #fe5c2b);
    position: absolute;
    top: 0;
    right: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    transform: translate(35%, -35%);
`

export const cardHoverCss = css`
    transition: all 0.3s ease-in-out;
    &:hover {
        cursor: pointer;
        border-color: var(--text-primary, transparent);
        box-shadow: 10px 10px 40px 0px var(--card-hover-shadow, rgba(0, 0, 0, 0.15));
    }
`

const typeCardStyle = css`
    border-radius: 10px;
    width: 138px;
    height: 124px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    ${cardHoverCss}
`

export const BillTypeWrapper = styled.div`
    position: relative;
    border: 1px solid #595959;

    ${typeCardStyle}
`

const StyledColumn = styled(Column)`
    max-width: 512px;
    margin: 0 auto;
    min-width: 512px;
    ${breakpoints.small} {
        min-width: unset;
    }
`

const BillTypeCard: React.VFC<{ type: BillSelect; count: number }> = ({ type, count }) => {
    const { icon, title } = type
    const { addBillOfType } = useLinkBillContext()
    return (
        <BillTypeWrapper onClick={() => addBillOfType(type)}>
            {count > 0 && (
                <Circle>
                    <Body.Med color="white" size={16}>
                        {count}
                    </Body.Med>
                </Circle>
            )}
            {icon ? icon : <BillIcon type={type.type} />}
            <Spacer height={8} />
            <Body.SmBold>{title}</Body.SmBold>
        </BillTypeWrapper>
    )
}

const Grid = styled.div`
    display: grid;
    column-gap: 20px;
    row-gap: 20px;
    grid-template-columns: 1fr 1fr 1fr;

    ${breakpoints.xsmall} {
        grid-template-columns: 1fr 1fr;
    }
`

const AddNewCard = () => {
    const { setStep, setSelectBillType } = useLinkBillContext()

    const handleSelect = () => {
        setSelectBillType(null)
        setStep(LinkBill.AddNewBill)
    }

    return (
        <BillTypeWrapper onClick={handleSelect}>
            <Icon src={themedImage('billTypeMore', plus)} />
            <Spacer height={8} />
            <Body.SmBold>Another bill</Body.SmBold>
        </BillTypeWrapper>
    )
}

const TypeCards = ({ billsOfType }: { billsOfType: Record<string, number> }) => {
    return (
        <Grid>
            {billTypes.map((type, index) => (
                <BillTypeCard key={index} type={type} count={billsOfType[type.type] ?? 0} />
            ))}
            <AddNewCard />
        </Grid>
    )
}

export const SelectBillType = ({
    selectBillHeader,
}: {
    selectBillHeader?: (foundBills: boolean, billCount?: string) => React.ReactNode
}) => {
    const { isDesktop } = useScreenSize()
    const [checked, setChecked] = useState(false)
    const { bills, showPopularBills, loading } = useLinkBillContext()
    const foundBills = bills.length > 0
    const numBillsText = bills.length === 1 ? '1 bill' : `${bills.length} bills`

    const component = selectBillHeader ? selectBillHeader?.(foundBills, numBillsText) : null

    const types = billTypes.filter((type) =>
        bills.find((bill) => bill.type === (type.type as BillType))
    )

    const billsOfType = useMemo(() => {
        const countMap: Record<string, number> = {}
        const billTypes = types.map(({ type }) => type)
        for (const billType of billTypes) {
            const typedBills = bills.filter((bill) => bill.type === (billType as BillType))
            countMap[billType] = typedBills.length
        }
        return countMap
    }, [bills, types])

    if (loading && showPopularBills) return <PageLoading />

    return (
        <StyledColumn align="center">
            {component}
            <Spacer height={32} />
            {showPopularBills && isDesktop && (
                <Container>
                    <Row align="flex-start">
                        <Body.Med size={14}>Here are some of our most popular bills</Body.Med>
                    </Row>
                    <Spacer height={16} />
                    <PopularBills />
                    <Spacer height={32} />
                    <Row align="flex-start">
                        <Body.Med size={14}>Explore all our different bill categories</Body.Med>
                    </Row>
                    <Spacer height={16} />
                </Container>
            )}
            <TypeCards billsOfType={billsOfType} />
            {!showPopularBills && isDesktop && (
                <Container>
                    <Spacer height={32} />
                    <Row align="center">
                        <Body.SmBold size={16}>Show popular bills</Body.SmBold>
                        <Spacer width={16} />
                        <Switch checked={checked} onClick={() => setChecked((c) => !c)} />
                    </Row>
                    <FadePresence renderKey={checked} style={{ width: '100%' }}>
                        {checked && (
                            <>
                                <Spacer height={16} />
                                <PopularBills />
                            </>
                        )}
                    </FadePresence>
                </Container>
            )}
        </StyledColumn>
    )
}
